import { addYears, format } from "date-fns";
import { useFormik } from "formik";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDomainHandler } from "hooks/useDomainHandler";
import { useGroups } from "hooks/useGroups";
import { useModal } from "hooks/useModal";
import { useProduct } from "hooks/useProduct";
import { useLanguage } from "hooks/useSelectedLanguage";

import { CombiLicenseProducts, LicenseDurationPerType, ProductType } from "constants/hst-constants";
import { TeacherModals } from "constants/modals-constants";

import { InputWithErrorMessage } from "components/InputWithErrorMessage";
import { MultiStepModal } from "components/MultiStepModal";
import { MultipleEmailInput } from "components/MultipleEmailInput/MultipleEmailInput";
import { Note } from "components/Note/Note";
import { RadioButton } from "components/RadioButton/RadioButton";
import { Select } from "components/SelectUI/Select";

import type { SelectOptions } from "components/SelectUI/Select.model";
import type { ProductDetails, ProductSkillLevel } from "models/product/ProductDetails";

import { Heading4 } from "styles/elements/Headings";
import { ParagraphLarge, ParagraphMedium } from "styles/elements/Texts";
import { Box, CssFlex, CssInput } from "styles/helpers/layout";

import {
  type NewEmailInviteBundleFormFields,
  buildNewEmailInviteBundleFormSchema,
} from "./CreateEmailInviteModal.schema";

export const CreateEmailInviteModal: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { isModalOpen, closeModal } = useModal();
  const { getStudentTermByDomain } = useDomainHandler();
  const { teacherGroups, getTeacherGroups, createGroup } = useGroups();
  const {
    productsList,
    fetchProducts,
    fetchProductDetails,
    fetchInvitationBundleList,
    postInviteStudents: createEmailInviteBundle,
  } = useProduct();

  const isCurrentlyOpen = isModalOpen(TeacherModals.CreateEmailInviteBundle);

  const [selectedBusinessProductDetails, setSelectedBusinessProductDetails] =
    useState<ProductDetails | null>(null);

  useEffect(() => {
    if (isCurrentlyOpen) {
      fetchProducts();
      getTeacherGroups(true);
    }
  }, [isCurrentlyOpen]);

  const groupSelectOptions = useMemo(() => {
    if (!teacherGroups) {
      return [];
    }

    return teacherGroups.map((group) => ({
      value: group.id,
      label: group.name,
    }));
  }, [teacherGroups]);

  const productSelectOptions: SelectOptions[] = useMemo(() => {
    if (!productsList) {
      return [];
    }

    const isProductExpired = (licenseExpirationDate?: string | Date) => {
      if (!licenseExpirationDate) {
        return false;
      }
      const expirationDate = new Date(licenseExpirationDate);
      return expirationDate < new Date();
    };

    const ddlOptions: SelectOptions[] = productsList.map((prod) => {
      const expired = isProductExpired(prod.licenseExpirationDate);

      return {
        label: prod.title,
        value: `${prod.id}`,
        disabled: expired,
        ...(expired && {
          hoverMessage: t(
            "modal:createEmailInviteBundleModal.steps.productSelection.fields.selectedProducts.tooltip.expiredLicense",
          ),
        }),
      };
    });

    // Only show combi license option if teacher owns an active license for both english and dutch
    const canSendCombiLicense = CombiLicenseProducts.every((productId) => {
      const ownedLicense = productsList.find(({ id }) => id === productId);
      return !!ownedLicense && !isProductExpired(ownedLicense.licenseExpirationDate);
    });

    if (canSendCombiLicense) {
      ddlOptions.push({
        label: t(
          "modal:createEmailInviteBundleModal.steps.productSelection.fields.selectedProducts.options.combiLicense",
        ),
        // Apply ascending order to product ids for combined option
        value: CombiLicenseProducts.sort((a, b) => a - b).join(","),
      });
    }

    return ddlOptions;
  }, [productsList, currentLanguage]);

  const formik = useFormik<NewEmailInviteBundleFormFields>({
    initialValues: {
      studentEmails: [],
      selectedProducts: [],
      licenseExpirationDate: addYears(new Date(), 1),
      groupId: undefined,
      groupAssignmentMethod: "new",
    },
    validationSchema: buildNewEmailInviteBundleFormSchema(),
    validateOnChange: true,
    onSubmit: async (values) => {
      // Call endpoint to create the group (only if user selected option to add to new)
      // or pick selected group id from form values if existing option is selected
      let groupId: number | undefined;
      if (values.groupAssignmentMethod === "new") {
        groupId = await createGroup(values.newGroupName);
      } else if (values.groupAssignmentMethod === "existing") {
        groupId = values.groupId && +values.groupId;
      }

      // Create invite bundle with the selected settings
      await createEmailInviteBundle(
        {
          groupId,
          licenseExpirationDate: values.licenseExpirationDate.toISOString(),
          productNames: values.selectedProducts
            .map((selectedProduct) => selectedProduct.name)
            .join(", "),
          productsList: values.selectedProducts.map((selectedProduct) => +selectedProduct.id),
          studentsEmailList: values.studentEmails,
          initialSkillLevel:
            values.selectedProducts.length === 1
              ? values.selectedProducts[0].initialSkillLevel
              : undefined,
        },
        {
          showToastOnSuccess: true,
        },
      );

      // Force refresh of invite list
      fetchInvitationBundleList();

      // Reset form fields after submit is complete
      formik.setSubmitting(false);
      formik.resetForm();
      closeModal();
    },
  });

  // Pre-select option to assign users to existing group if there are available groups,
  // otherwise fall back to creating a new group
  useEffect(() => {
    if (groupSelectOptions.length > 0) {
      formik.setFieldValue("groupAssignmentMethod", "existing");
    } else {
      formik.setFieldValue("groupAssignmentMethod", "new");
    }
  }, [formik.setFieldValue, groupSelectOptions]);

  useEffect(() => {
    const isBusinessLicense = formik.values.selectedProducts.some(
      ({ type }) => type === ProductType.BUSINESS,
    );

    // Fetch product details for business product so user can choose initial goal level
    if (formik.values.selectedProducts.length === 1 && isBusinessLicense) {
      fetchProductDetails(formik.values.selectedProducts[0].id, {
        skipSaveResultInStore: true,
      }).then((businessProductDetails) =>
        setSelectedBusinessProductDetails(businessProductDetails),
      );
    }

    // Expiration date will vary based on whether selected product is business or not
    const selectedLicenseType = isBusinessLicense ? ProductType.BUSINESS : ProductType.REGULAR;
    formik.setFieldValue(
      "licenseExpirationDate",
      addYears(new Date(), LicenseDurationPerType[selectedLicenseType]),
    );
  }, [formik.setFieldValue, formik.values.selectedProducts]);

  const availableSkillLevels: ProductSkillLevel[] = useMemo(() => {
    if (
      selectedBusinessProductDetails &&
      formik.values.selectedProducts.length === 1 &&
      formik.values.selectedProducts[0].type === ProductType.BUSINESS &&
      formik.values.selectedProducts[0].id === selectedBusinessProductDetails.id
    ) {
      return (
        selectedBusinessProductDetails.levels.filter((level) => level.selectable) ||
        ([] as ProductSkillLevel[])
      );
    }
    return [];
  }, [formik.values.selectedProducts, selectedBusinessProductDetails]);

  const createEmailInviteBundleSteps = [
    {
      id: "add-students-step",
      label: t("modal:createEmailInviteBundleModal.steps.addStudents.title", {
        studentTerm: getStudentTermByDomain({ usePlural: true }),
      }),
      iconName: "fas fa-paper-plane",
      content: (
        <CssFlex flexDirection="column" gap={1.5}>
          <CssFlex flexDirection="column" gap={0.5}>
            <Heading4>
              {t("modal:createEmailInviteBundleModal.steps.addStudents.title", {
                studentTerm: getStudentTermByDomain({ usePlural: true }),
              })}
            </Heading4>
            <ParagraphLarge>
              {t("modal:createEmailInviteBundleModal.steps.addStudents.description", {
                studentTerm: getStudentTermByDomain({ usePlural: true }),
              })}
            </ParagraphLarge>
            <MultipleEmailInput
              dataCy="group-student-multiple-email-input"
              pasteAreaPlaceholder={t(
                "modal:createEmailInviteBundleModal.steps.addStudents.fields.studentEmails.placeholder.pasteArea",
              )}
              selectedEmails={formik.values.studentEmails.map((email) => ({
                email,
                isIncorrect: false,
              }))}
              onSelectedEmailsChanged={(emails) => {
                formik.setFieldValue(
                  "studentEmails",
                  emails.map(({ email }) => email),
                );
              }}
            />
          </CssFlex>
          <Note>
            {t("modal:createEmailInviteBundleModal.steps.addStudents.activationEmailNote", {
              studentTerm: getStudentTermByDomain({ usePlural: true }),
            })}
          </Note>
        </CssFlex>
      ),
      disableNextButton: !formik.dirty || !!formik.errors.studentEmails,
    },
    {
      id: "select-product-step",
      label: t("modal:createEmailInviteBundleModal.steps.productSelection.title", {
        studentTerm: getStudentTermByDomain({ usePlural: true }),
      }),
      iconName: "fas fa-books",
      content: (
        <CssFlex flexDirection="column" gap={1.5}>
          <CssFlex flexDirection="column" gap={0.5}>
            <Heading4>
              {t("modal:createEmailInviteBundleModal.steps.productSelection.title")}
            </Heading4>
            <ParagraphLarge>
              {t("modal:createEmailInviteBundleModal.steps.productSelection.description", {
                studentTerm: getStudentTermByDomain({ usePlural: true }),
              })}
            </ParagraphLarge>
            <CssFlex flexDirection="column" gap={1.5} paddingTop="1rem" paddingBottom="1rem">
              <Box>
                <label
                  id="create-email-invite-bundle-product-selector-label"
                  htmlFor="create-email-invite-bundle-product-selector"
                >
                  {t(
                    "modal:createEmailInviteBundleModal.steps.productSelection.fields.selectedProducts.label",
                  )}
                </label>
                <Select
                  showSearchBox
                  aria-labelledby="create-email-invite-bundle-product-selector-label"
                  dataCy="product-selector"
                  id="create-email-invite-bundle-product-selector"
                  options={productSelectOptions}
                  name="selectedProducts"
                  placeholder={t(
                    "modal:createEmailInviteBundleModal.steps.productSelection.fields.selectedProducts.placeholder",
                  )}
                  showPlaceholderInOptionList={false}
                  value={formik.values.selectedProducts.map(({ id }) => id).join(",")}
                  onChange={(e) => {
                    // To prevent issues with combi option if products are returned in different order,
                    // we must make sure to sort ids in ascending order when setting selected value
                    formik.setFieldValue(
                      "selectedProducts",
                      productsList
                        ?.filter(({ id: productId }) =>
                          e.target.value.split(",").includes(`${productId}`),
                        )
                        .map((product) => ({
                          id: product.id,
                          name: product.title,
                          type: product.type,
                        }))
                        .sort((a, b) => a.id - b.id) || [],
                    );
                  }}
                />
              </Box>
              {/* -- License expiration date is read-only and will be set automatically based on selected product -- */}
              <Box>
                <label htmlFor="create-email-invite-bundle-license-expiration-date-input">
                  {t(
                    "modal:createEmailInviteBundleModal.steps.productSelection.fields.licenseExpirationDate.label",
                  )}
                </label>
                <CssInput
                  readOnly
                  disabled
                  dataCy="license-expiration-date-input"
                  id="create-email-invite-bundle-license-expiration-date-input"
                  name="licenseExpirationDate"
                  type="date"
                  value={format(formik.values.licenseExpirationDate, "yyyy-MM-dd")}
                />
              </Box>
            </CssFlex>
            {/* -- Initial skill level (available only when one business prod is selected) -- */}
            {formik.values.selectedProducts.length === 1 &&
              formik.values.selectedProducts[0].type === ProductType.BUSINESS && (
                <>
                  <label htmlFor="create-email-invite-bundle-initial-skill-level-input">
                    {t(
                      "modal:createEmailInviteBundleModal.steps.productSelection.fields.initialSkillLevel.label",
                    )}
                  </label>
                  <ParagraphMedium>
                    {t(
                      "modal:createEmailInviteBundleModal.steps.productSelection.fields.initialSkillLevel.description",
                    )}
                  </ParagraphMedium>
                  {availableSkillLevels.map((productSkillLevel) => (
                    <RadioButton
                      bulletColor="primary"
                      bulletStyle="thin"
                      key={productSkillLevel.id}
                      checked={
                        productSkillLevel.level ===
                        formik.values.selectedProducts[0].initialSkillLevel
                      }
                      dataCy="initial-skill-level-radio"
                      description={productSkillLevel.description || ""}
                      disabled={false}
                      label={productSkillLevel.label}
                      name="initialSkillLevel"
                      value={productSkillLevel.level}
                      onChange={(e) => {
                        formik.setFieldValue(
                          "selectedProducts",
                          formik.values.selectedProducts.map((selectedProduct) => ({
                            ...selectedProduct,
                            initialSkillLevel: +e.target.value,
                          })),
                        );
                      }}
                    />
                  ))}
                </>
              )}
          </CssFlex>
          <Note>
            {t("modal:createEmailInviteBundleModal.steps.productSelection.activationNote", {
              studentTerm: getStudentTermByDomain({ usePlural: true }),
            })}
          </Note>
        </CssFlex>
      ),
      disableNextButton: !formik.dirty || !!formik.errors.selectedProducts,
    },
    {
      id: "assign-group-step",
      label: t("modal:createEmailInviteBundleModal.steps.assignGroup.title"),
      iconName: "fas fa-users",
      content: (
        <form noValidate onSubmit={formik.handleSubmit}>
          <CssFlex flexDirection="column" gap={0.5}>
            <Heading4>{t("modal:createEmailInviteBundleModal.steps.assignGroup.title")}</Heading4>
            <ParagraphLarge>
              {t("modal:createEmailInviteBundleModal.steps.assignGroup.description", {
                studentTerm: getStudentTermByDomain({ usePlural: true }),
              })}
            </ParagraphLarge>
            <CssFlex flexDirection="column" gap={1.5} paddingTop="1.5rem">
              <RadioButton
                bulletColor="primary"
                bulletStyle="thin"
                checked={formik.values.groupAssignmentMethod === "existing"}
                disabled={groupSelectOptions.length === 0}
                dataCy="assign-to-existing-group-radio"
                label={t(
                  "modal:createEmailInviteBundleModal.steps.assignGroup.fields.groupAssignmentMethod.options.existing",
                )}
                name="groupAssignmentMethod"
                value="existing"
                onChange={formik.handleChange}
              />
              {formik.values.groupAssignmentMethod === "existing" && (
                <Box paddingLeft="4.5rem" paddingBottom="1rem">
                  <label
                    id="create-email-invite-bundle-group-selector-label"
                    htmlFor="create-email-invite-bundle-group-selector"
                  >
                    {t("modal:createEmailInviteBundleModal.steps.assignGroup.fields.groupId.label")}
                  </label>
                  <Select
                    showSearchBox
                    id="create-email-invite-bundle-group-selector"
                    aria-labelledby="create-email-invite-bundle-group-selector-label"
                    dataCy="group-selector"
                    options={groupSelectOptions}
                    name="groupId"
                    showPlaceholderInOptionList={false}
                    value={formik.values.groupId}
                    onChange={formik.handleChange}
                  />
                </Box>
              )}
              <RadioButton
                bulletColor="primary"
                bulletStyle="thin"
                checked={formik.values.groupAssignmentMethod === "new"}
                dataCy="assign-to-new-group-radio"
                label={t(
                  "modal:createEmailInviteBundleModal.steps.assignGroup.fields.groupAssignmentMethod.options.new",
                )}
                name="groupAssignmentMethod"
                value="new"
                onChange={formik.handleChange}
              />
              {formik.values.groupAssignmentMethod === "new" && (
                <Box paddingLeft="4.5rem" paddingBottom="1rem">
                  <label htmlFor="create-email-invite-bundle-group-name-input">
                    {t(
                      "modal:createEmailInviteBundleModal.steps.assignGroup.fields.newGroupName.label",
                    )}
                    *
                  </label>
                  <InputWithErrorMessage
                    dataCy="group-name-input"
                    errorMessage={formik.errors.newGroupName}
                    hasError={!!(formik.errors.newGroupName && formik.touched.newGroupName)}
                    id="create-email-invite-bundle-group-name-input"
                    name="newGroupName"
                    value={formik.values.newGroupName}
                    onChange={formik.handleChange}
                  />
                </Box>
              )}
              <RadioButton
                bulletColor="primary"
                bulletStyle="thin"
                checked={formik.values.groupAssignmentMethod === "none"}
                dataCy="assign-to-no-group-radio"
                label={t(
                  "modal:createEmailInviteBundleModal.steps.assignGroup.fields.groupAssignmentMethod.options.none",
                )}
                name="groupAssignmentMethod"
                value="none"
                onChange={formik.handleChange}
              />
              {formik.values.groupAssignmentMethod === "new" && (
                <Note>
                  {t("modal:createEmailInviteBundleModal.steps.assignGroup.addToNewGroupNote", {
                    studentTerm: getStudentTermByDomain({ usePlural: true }),
                  })}
                </Note>
              )}
              {formik.values.groupAssignmentMethod === "existing" && (
                <Note>
                  {t(
                    "modal:createEmailInviteBundleModal.steps.assignGroup.addToExistingGroupNote",
                    {
                      studentTerm: getStudentTermByDomain({ usePlural: true }),
                    },
                  )}
                </Note>
              )}
              {formik.isValid && (
                <Note variant="warning">
                  {t("modal:createEmailInviteBundleModal.steps.assignGroup.invoiceWarning")}
                </Note>
              )}
            </CssFlex>
          </CssFlex>
        </form>
      ),
      nextButtonLabel: t("modal:createEmailInviteBundleModal.steps.assignGroup.nextButton"),
      disableNextButton: !formik.dirty || !formik.isValid || formik.isSubmitting,
      onNextButtonClick: () => {
        formik.setSubmitting(true);
        formik.submitForm();
      },
    },
  ];

  return (
    <MultiStepModal
      id={TeacherModals.CreateEmailInviteBundle}
      title={t("modal:createEmailInviteBundleModal.title")}
      steps={createEmailInviteBundleSteps}
      onClose={() => formik.resetForm()}
      autoCloseOnLastNextButtonClick={false}
    />
  );
};
